// The Share button on a POI's page.
//
// Side effects only — which link to share and what text goes with it is
// `poiSharing`, so the page and the map dialog say exactly the same thing.
//
// The button carries the link in a data attribute rather than fetching it,
// because the server already built it for the canonical tag on the same page.

import { poiSharePayload, poiShareText } from '../../utils/helpers/poiSharing';

const SELECTOR = '[data-poi-share]';
const FEEDBACK_MS = 2000;

function poiFrom(button: HTMLElement) {
  return {
    name: button.dataset.shareTitle || '',
    shareUrl: button.dataset.shareUrl || '',
  };
}

// A brief label swap rather than a toast: this page has no toast container, and
// the button is what the walker is looking at.
function flash(button: HTMLElement, message: string) {
  const original = button.innerHTML;
  button.textContent = message;
  window.setTimeout(() => {
    button.innerHTML = original;
  }, FEEDBACK_MS);
}

async function share(button: HTMLElement) {
  const poi = poiFrom(button);
  const payload = poiSharePayload(poi);
  if (!payload) return;

  if (typeof navigator.share === 'function') {
    try {
      await navigator.share(payload);
    } catch (e: any) {
      // A cancelled share sheet throws AbortError. That is the walker changing
      // their mind, not a failure, and must not fall through to a clipboard
      // write they did not ask for.
      if (e && e.name !== 'AbortError') flash(button, 'Could not share');
    }
    return;
  }

  try {
    await navigator.clipboard.writeText(poiShareText(poi) as string);
    flash(button, 'Link copied');
  } catch (e) {
    flash(button, 'Could not copy');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll<HTMLElement>(SELECTOR).forEach((button) => {
    button.addEventListener('click', () => share(button));
  });
});
